import { ApiError } from '../errors';
import { etagFromRow, get, getVersioned, patch, post, postVersioned, seg, type Query } from '../client';
import type { Coded, ListParams, Page, PolicyDecision, PurposeOfUse, Tone } from '../types/common';
import type {
  AccessCheck, AdministrativeSex, AllergyBannerStatus, BreakGlassGrant, BreakGlassReason, BreakGlassRequest, BreakGlassScope, Consent, ConsentStatus,
  CreateConsentRequest, CreatePatientRequest, CreateProxyAuthorityRequest, CreateRelationshipRequest, CreateSensitivityLabelRequest, DuplicateCandidate,
  IdentityCandidate, IdentityDispositionRequest, IdentityStatus, MergePatientsRequest, PatchPatientRequest, Patient, PatientAddressDto, PatientBannerData,
  PatientContactDto, PatientIdentifier, PatientListItem, PatientMerge, PatientSearchParams, ProxyAuthority, Relationship, SensitivityLabel, SourceLabel,
  UnmergeAssignment, UnmergeReference, VerificationStatus,
} from '../types/patients';

/* =====================================================================================================
 * Boundary adapters: core-api patient-identity DTOs → the UI's Patient / PatientListItem / ... types.
 * Every normalizer tolerates both the backend shape and the (older) UI/mock shape, and never throws on
 * missing fields — the chart frame and the banner depend on this.
 * ===================================================================================================== */

/* eslint-disable @typescript-eslint/no-explicit-any */
type Raw = Record<string, any>;

export const MRN_SYSTEM = 'urn:etnas:mrn';
const isObj = (v: unknown): v is Raw => !!v && typeof v === 'object' && !Array.isArray(v);
const arr = <T = Raw>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : d);
const optStr = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
const dateOnly = (v: unknown): string => str(v).slice(0, 10);
const rv = (v: unknown): string | undefined => (v === null || v === undefined || v === '' ? undefined : String(v));
const tagOf = (raw: Raw, etag?: string | null) => etag ?? etagFromRow(rv(raw.rowVersion));

/** "58 y", "7 y", "11 mo" — same rules as lib/age (kept local so the API layer has no UI imports). */
export function displayAge(dob: string, ref = new Date()): string {
  const b = new Date(`${dob}T00:00:00`);
  if (!dob || isNaN(b.getTime())) return '';
  let y = ref.getFullYear() - b.getFullYear();
  const md = ref.getMonth() - b.getMonth();
  if (md < 0 || (md === 0 && ref.getDate() < b.getDate())) y--;
  if (y >= 2) return `${y} y`;
  const months = (ref.getFullYear() - b.getFullYear()) * 12 + md - (ref.getDate() < b.getDate() ? 1 : 0);
  return `${Math.max(0, months)} mo`;
}

/** Languages: backend stores a short code (seed: "en"); the UI shows names. */
const LANGUAGE_NAME: Record<string, string> = {
  en: 'English', es: 'Spanish', vi: 'Vietnamese', zh: 'Mandarin', ar: 'Arabic', pl: 'Polish', ig: 'Igbo', fr: 'French', ru: 'Russian', ko: 'Korean', tl: 'Tagalog', ht: 'Haitian Creole',
};
export const languageName = (code: unknown) => {
  const c = str(code);
  return LANGUAGE_NAME[c.toLowerCase()] ?? c;
};
export const languageCode = (name: string) => {
  const hit = Object.entries(LANGUAGE_NAME).find(([, n]) => n.toLowerCase() === name.trim().toLowerCase());
  return (hit ? hit[0] : name.trim()).slice(0, 20);
};

const ID_METHOD_LABEL: Record<string, string> = {
  drivers_license: "Driver's license", passport: 'Passport', state_id: 'State ID card', known_patient: 'Known patient (staff recognise)',
};
const EXTERNAL_SOURCES = /^(hie|external|interface|import|payer|fhir|hl7)/i;
const PATIENT_SOURCES = /^(patient|portal|self|kiosk)/i;

/** Backend per-element provenance → UI SourceLabel (patient-declared vs verified stay distinguishable). */
export function sourceLabelOf(p: Raw | undefined | null): SourceLabel | undefined {
  if (!isObj(p)) return undefined;
  const vs = str(p.verificationStatus) as VerificationStatus | '';
  const source = str(p.source);
  if (!vs && !source) return undefined;
  const kind: SourceLabel['source'] =
    vs === 'verified' ? 'verified' : vs === 'patient_reported' || PATIENT_SOURCES.test(source) ? 'patient_declared' : EXTERNAL_SOURCES.test(source) ? 'external' : 'staff_entered';
  return { source: kind, at: optStr(p.verifiedAt), method: kind === 'verified' ? ID_METHOD_LABEL[source] : undefined };
}

/** UI SourceLabel → backend provenance fields. */
function provenanceOf(l: SourceLabel | undefined, fallback: 'unverified' | 'patient_reported' = 'unverified') {
  if (!l) return { source: 'manual', verificationStatus: fallback as VerificationStatus };
  if (l.source === 'verified') return { source: methodCode(l.method) ?? 'manual', verificationStatus: 'verified' as const, verifiedAt: l.at ?? new Date().toISOString() };
  if (l.source === 'patient_declared') return { source: 'patient', verificationStatus: 'patient_reported' as const };
  if (l.source === 'external') return { source: 'external', verificationStatus: 'unverified' as const };
  return { source: 'staff', verificationStatus: 'unverified' as const };
}
const methodCode = (label?: string) => {
  if (!label) return undefined;
  const hit = Object.entries(ID_METHOD_LABEL).find(([k, v]) => v === label || k === label);
  return hit ? hit[0] : label.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 80);
};

const ALLERGY_STATUS: Record<string, AllergyBannerStatus> = {
  list: 'list', has_allergies: 'list', allergies: 'list', active: 'list',
  nka: 'nka', no_known_allergies: 'nka', nkda: 'nkda', no_known_drug_allergies: 'nkda',
  'not-assessed': 'not-assessed', not_assessed: 'not-assessed', unknown: 'not-assessed',
};
const SEVERITIES = ['severe', 'moderate', 'mild', 'intolerance'] as const;

/** Banner with safe defaults. Reads extended backend banner facts (allergies, encounter, flags) when present. */
export function normalizePatientBanner(b: unknown, ctx: { deceased?: boolean; status?: string; sensitive?: boolean; deceasedAt?: string | null } = {}): PatientBannerData {
  const raw: Raw = isObj(b) ? b : {};
  const allergyStatus = ALLERGY_STATUS[str(raw.allergyStatus).toLowerCase()] ?? 'not-assessed';
  /** Backend sends allergyStatus "restricted" when allergies are withheld from the caller. */
  const allergiesRestricted = str(raw.allergyStatus) === 'restricted';
  const allergies = arr(raw.allergies)
    .filter(isObj)
    .map((a) => {
      const sev = str(a.severity ?? a.criticality).toLowerCase();
      return {
        substance: str(isObj(a.substance) ? a.substance.display : a.substance ?? a.display, 'Unknown'),
        reaction: str(isObj(a.reaction) ? a.reaction.display : a.reaction).toLowerCase(),
        severity: (SEVERITIES as readonly string[]).includes(sev) ? (sev as (typeof SEVERITIES)[number]) : sev === 'high' ? 'severe' : sev === 'low' ? 'mild' : 'moderate',
      };
    });
  const flags: PatientBannerData['flags'] = [];
  for (const f of arr<unknown>(raw.flags)) {
    if (typeof f === 'string' && f) flags.push({ label: f });
    else if (isObj(f) && str(f.label ?? f.display)) flags.push({ label: str(f.label ?? f.display), tone: optStr(f.tone) as Tone | undefined, icon: optStr(f.icon) });
  }
  const has = (re: RegExp) => flags.some((f) => re.test(f.label));
  const deceased = raw.deceased ?? ctx.deceased;
  if (deceased && !has(/deceased/i)) flags.unshift({ label: `Deceased${ctx.deceasedAt ? ' ' + dateOnly(ctx.deceasedAt) : ''}`, tone: 'muted' });
  const status = str(raw.status ?? ctx.status);
  if (status === 'merged' && !has(/merged/i)) flags.unshift({ label: 'Merged record — use the surviving chart', tone: 'urgent', icon: 'urgent' });
  if ((raw.twinAlert || raw.multipleBirth) && !has(/twin/i)) flags.push({ label: 'Twin on file', tone: 'accent', icon: 'users' });
  if ((raw.duplicateAlert || raw.possibleDuplicate) && !has(/duplicate/i)) flags.push({ label: 'Possible duplicate record', tone: 'urgent', icon: 'urgent' });
  if (raw.breakGlassActive && !has(/emergency access/i)) flags.unshift({ label: 'Emergency access active', tone: 'critical', icon: 'glass' });
  const e = isObj(raw.encounter) ? raw.encounter : isObj(raw.activeEncounter) ? raw.activeEncounter : null;
  const encounter = e
    ? {
        id: str(e.id),
        type: str(isObj(e.type) ? e.type.display : e.type ?? e.class, 'Encounter'),
        place: str(e.place ?? [e.locationName, e.room].filter(Boolean).join(' · ')) || humanize(str(isObj(e.type) ? e.type.display : e.type ?? e.class, 'Encounter')),
        attending: str(isObj(e.attending) ? e.attending.display : e.attending ?? (isObj(e.provider) ? e.provider.display : e.provider)),
        since: str(e.since ?? (e.start || e.startedAt ? `Since ${new Date(str(e.start ?? e.startedAt)).toTimeString().slice(0, 5)}` : '')),
      }
    : null;
  return {
    allergyStatus,
    allergies,
    allergyVerifiedAt: optStr(raw.allergyVerifiedAt) ?? null,
    flags,
    encounter,
    codeStatus: optStr(raw.codeStatus),
    careTeam: optStr(raw.careTeam),
    coverage: optStr(raw.coverage),
    proxy: optStr(raw.proxy),
    restricted: !!(raw.restricted ?? false) || allergiesRestricted,
  };
}

const humanize = (s: string) => s.replace(/[_-]+/g, ' ').replace(/^./, (x) => x.toUpperCase());
/** core-api masks contact values on read (e.g. "***0123"). */
export const isMaskedValue = (v: unknown) => typeof v === 'string' && v.startsWith('***');

const identityStatusOf = (v: unknown): IdentityStatus => {
  const s = str(v);
  return s === 'verified' || s === 'temporary' || s === 'confidential' ? s : 'unverified';
};
const SEXES: AdministrativeSex[] = ['female', 'male', 'other', 'unknown'];
const sexOf = (v: unknown): AdministrativeSex => (SEXES.includes(v as AdministrativeSex) ? (v as AdministrativeSex) : 'unknown');

/** Already the UI/mock shape? (name.display present) */
const isUiPatient = (raw: Raw) => isObj(raw.name) && typeof raw.name.display === 'string';

function mrnOf(raw: Raw): string {
  const ids = arr(raw.identifiers);
  const hit = ids.find((i) => i.system === MRN_SYSTEM) ?? ids.find((i) => /mrn/i.test(str(i.system)));
  return str(hit?.value ?? raw.mrn);
}

function nameOf(raw: Raw) {
  const given = str(raw.givenName).trim();
  const family = str(raw.familyName).trim();
  const display = family && given ? `${family}, ${given}` : family || given || 'Name not on file';
  return { family: family || display, given: given ? given.split(/\s+/) : [], preferred: optStr(raw.preferredName), display };
}

/** Backend list/search item (or UI item) → PatientListItem. */
export function normalizePatientListItem(input: unknown): PatientListItem {
  const raw: Raw = isObj(input) ? input : {};
  if (isUiPatient(raw)) {
    return {
      ...(raw as PatientListItem),
      name: { family: str(raw.name.family), given: arr<string>(raw.name.given), preferred: optStr(raw.name.preferred), display: str(raw.name.display) },
      age: str(raw.age) || displayAge(str(raw.dob)),
      identityStatus: identityStatusOf(raw.identityStatus),
      sex: sexOf(raw.sex),
      deceased: !!raw.deceased,
      sensitive: !!raw.sensitive,
      etag: raw.etag ?? etagFromRow(rv(raw.rowVersion ?? raw._v)),
    };
  }
  const banner: Raw = isObj(raw.banner) ? raw.banner : {};
  const dob = dateOnly(raw.birthDate ?? raw.dob);
  const contacts = arr<PatientContactDto>(raw.contacts);
  const phone = contacts.find((c) => c.system === 'phone')?.value;
  const verification = (str(banner.verificationStatus ?? raw.verificationStatus) || undefined) as VerificationStatus | undefined;
  return {
    id: str(raw.id),
    rowVersion: rv(raw.rowVersion),
    etag: tagOf(raw),
    status: optStr(raw.status ?? banner.status),
    mergedIntoId: optStr(raw.mergedIntoId) ?? null,
    verificationStatus: verification,
    mrn: mrnOf(raw),
    name: nameOf(raw),
    dob,
    age: displayAge(dob),
    sex: sexOf(raw.sex),
    identityStatus: identityStatusOf(banner.identityStatus ?? verification),
    deceased: !!(banner.deceased ?? raw.deceased),
    sensitive: !!banner.sensitive || arr(raw.sensitivityLabels).length > 0,
    phoneLast4: phone ? phone.replace(/\D/g, '').slice(-4) : undefined,
    city: optStr(arr<PatientAddressDto>(raw.addresses)[0]?.city),
    lastSeenAt: optStr(raw.lastSeenAt) ?? null,
  };
}

/** Backend patientView (or UI/mock Patient) → UI Patient. Never throws; banner always has safe defaults. */
export function normalizePatient(input: unknown, etag?: string | null): Patient {
  const raw: Raw = isObj(input) ? input : {};
  if (isUiPatient(raw)) {
    const li = normalizePatientListItem(raw);
    return {
      ...(raw as Patient),
      ...li,
      language: str(raw.language, 'English'),
      interpreterNeeded: !!raw.interpreterNeeded,
      sourceLabels: isObj(raw.sourceLabels) ? raw.sourceLabels : {},
      banner: normalizePatientBanner(raw.banner, { deceased: li.deceased, deceasedAt: raw.deceasedAt }),
      updatedAt: str(raw.updatedAt),
      etag: etag ?? li.etag ?? null,
      extendedDemographics: raw.extendedDemographics ?? true,
    };
  }
  const li = normalizePatientListItem(raw);
  const contacts = arr<PatientContactDto>(raw.contacts);
  const addresses = arr<PatientAddressDto>(raw.addresses);
  const names = arr(raw.names);
  const phoneC = contacts.find((c) => c.system === 'phone');
  const emailC = contacts.find((c) => c.system === 'email');
  const a0 = addresses[0];
  const ids = arr(raw.identifiers);
  const ssn = ids.find((i) => /ssn/i.test(str(i.system)));
  const patientProv = { source: raw.source, verificationStatus: raw.verificationStatus, verifiedAt: raw.verifiedAt };
  const official = names.find((n) => n.use === 'official') ?? names[0];
  const sourceLabels: Partial<Record<string, SourceLabel>> = {};
  const put = (k: string, v: SourceLabel | undefined) => {
    if (v) sourceLabels[k] = v;
  };
  put('name', sourceLabelOf(official ?? patientProv));
  put('dob', sourceLabelOf(patientProv));
  put('sex', sourceLabelOf(patientProv));
  put('language', raw.language ? sourceLabelOf(patientProv) : undefined);
  put('phone', sourceLabelOf(phoneC));
  put('email', sourceLabelOf(emailC));
  put('address', sourceLabelOf(a0));
  return {
    ...li,
    etag: etag ?? li.etag ?? null,
    language: languageName(raw.language) || 'English',
    interpreterNeeded: !!raw.interpreterNeeded,
    phone: phoneC?.value,
    email: emailC?.value,
    preferredChannel: undefined,
    address: a0
      ? { line1: str(arr<string>(a0.line)[0]), line2: optStr(arr<string>(a0.line)[1]), city: str(a0.city), state: str(a0.state), postalCode: str(a0.postalCode) }
      : undefined,
    emergencyContact: null,
    coverage: null,
    ssnLast4: ssn ? str(ssn.value).replace(/\D/g, '').slice(-4) || null : null,
    deceasedAt: optStr(raw.deceasedAt) ? dateOnly(raw.deceasedAt) : null,
    sourceLabels,
    banner: normalizePatientBanner(raw.banner, { deceased: li.deceased, status: li.status, sensitive: li.sensitive, deceasedAt: raw.deceasedAt }),
    updatedAt: str(raw.updatedAt ?? raw.createdAt),
    names: names as Patient['names'],
    contacts,
    addresses,
    identifiers: ids as Patient['identifiers'],
    sensitivityLabels: arr(raw.sensitivityLabels) as Patient['sensitivityLabels'],
    extendedDemographics: false,
    portalAccountId: optStr(raw.portalAccountId ?? raw.portalActorId ?? raw.portalIdentityId) ?? null,
  };
}

/* ---------------- Search (R-111) ---------------- */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
/** An MRN-like token: no spaces, contains a digit, letters/digits/dashes (e.g. SYN-NORTHWIND-0002, NW-101234). */
const MRN_LIKE = /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9-]{3,}$/;

/**
 * Turn what the person typed into backend search params. core-api `q` only substring-matches "given family",
 * so: YYYY-MM-DD or MM/DD/YYYY → dob; MRN-like tokens → mrn; "Family, Given" → "Given Family".
 */
export function buildPatientSearch(p: PatientSearchParams): { query: Query; reasons: string[]; reversed?: string } {
  const query: Query = { limit: p.limit, cursor: p.cursor, dob: p.dob, mrn: p.mrn };
  const reasons: string[] = [];
  let reversed: string | undefined;
  const raw = (p.q ?? '').trim();
  if (raw) {
    const nameParts: string[] = [];
    let commaName: string | null = null;
    const comma = raw.match(/^([^,\d]+),\s*([^,\d]+?)(?:\s+(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4}))?$/);
    if (comma) {
      commaName = `${comma[2].trim()} ${comma[1].trim()}`.replace(/\s+/g, ' ');
      if (comma[3]) query.dob = toIsoDate(comma[3]);
    } else {
      for (const tok of raw.split(/\s+/)) {
        const iso = toIsoDate(tok);
        if (iso) query.dob = iso;
        else if (MRN_LIKE.test(tok) && !query.mrn) query.mrn = tok.toUpperCase();
        else nameParts.push(tok.replace(/,$/, ''));
      }
    }
    const name = commaName ?? nameParts.join(' ');
    if (name) {
      query.q = name;
      reasons.push('Name');
      const words = name.split(' ');
      if (!commaName && words.length === 2) reversed = `${words[1]} ${words[0]}`;
    }
  }
  if (query.mrn) reasons.push('MRN');
  if (query.dob) reasons.push('Date of birth');
  return { query, reasons, reversed };
}
function toIsoDate(tok: string): string | undefined {
  if (ISO_DATE.test(tok)) return tok;
  const m = tok.match(US_DATE);
  return m ? `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}` : undefined;
}

function normalizePage<T>(page: unknown, fn: (x: unknown) => T): Page<T> {
  const p: Raw = isObj(page) ? page : {};
  return { items: arr(p.items).map(fn), nextCursor: (p.nextCursor as string | null | undefined) ?? null, ...(p.restricted !== undefined ? { restricted: Number(p.restricted) || 0 } : {}) };
}

/* ---------------- Create / patch bodies (R-007) ---------------- */

/** UI registration form model → strict backend POST /patients body. */
export function toCreatePatientBody(b: CreatePatientRequest): Raw {
  const labels = b.sourceLabels ?? {};
  const given = b.name.given.filter(Boolean).join(' ').trim();
  const contacts: Raw[] = [];
  if (b.phone) contacts.push({ system: 'phone', value: b.phone, use: 'mobile', ...provenanceOf(labels.phone, 'patient_reported') });
  if (b.email) contacts.push({ system: 'email', value: b.email, use: 'home', ...provenanceOf(labels.email, 'patient_reported') });
  const addresses: Raw[] = b.address
    ? [{
        line: [b.address.line1, b.address.line2].filter((x): x is string => !!x),
        city: b.address.city,
        ...(b.address.state ? { state: b.address.state } : {}),
        postalCode: b.address.postalCode,
        country: 'US',
        ...provenanceOf(labels.address, 'patient_reported'),
      }]
    : [];
  const nameProv = provenanceOf(labels.name, 'patient_reported');
  const body: Raw = {
    givenName: given,
    familyName: b.name.family.trim(),
    ...(b.name.preferred ? { preferredName: b.name.preferred } : {}),
    birthDate: b.dob,
    sex: b.sex,
    names: [{ givenName: given, familyName: b.name.family.trim(), use: 'official', ...nameProv }],
    addresses,
    contacts,
    language: languageCode(b.language ?? 'en'),
    identifiers: (b.identifiers ?? []).map((i) => ({ ...i, source: 'manual', verificationStatus: 'unverified' })),
    ...provenanceOf(labels.dob, 'patient_reported'),
  };
  if (b.dispositionOverride) body.dispositionOverride = { reasonCode: b.dispositionOverride.reasonCode, disposition: b.dispositionOverride.disposition };
  return body;
}

/** UI PATCH (form keys) → strict backend PATCH body. Contacts/addresses are whole arrays: merged into `current`. */
export function toPatchPatientBody(b: PatchPatientRequest, current?: Patient | null): Raw {
  const out: Raw = {};
  const labels = b.sourceLabels ?? {};
  if (b.name) {
    out.givenName = b.name.given.filter(Boolean).join(' ');
    out.familyName = b.name.family;
    if (b.name.preferred !== undefined) out.preferredName = b.name.preferred;
  }
  if (b.dob) out.birthDate = b.dob;
  if (b.sex) out.sex = b.sex;
  if (b.language !== undefined) out.language = languageCode(b.language || 'en');
  const verification: VerificationStatus | undefined = b.verificationStatus ?? (b.identityStatus === 'verified' ? 'verified' : b.identityStatus === 'unverified' ? 'unverified' : undefined);
  if (verification) {
    out.verificationStatus = verification;
    if (verification === 'verified') {
      out.verifiedAt = labels.name?.at ?? labels.dob?.at ?? new Date().toISOString();
      const m = methodCode(labels.name?.method ?? labels.dob?.method);
      if (m) out.source = m;
      // The legal name element carries its own provenance: mark the official name verified too.
      const names = arr(current?.names);
      if (names.length && !b.name) {
        out.names = names.map((n, i) =>
          stripUndefined(n.use === 'official' || (i === 0 && !names.some((x) => x.use === 'official')) ? { ...n, verificationStatus: 'verified', verifiedAt: out.verifiedAt, source: m ?? n.source ?? 'manual' } : n),
        );
      }
    }
  }
  if (b.phone !== undefined || b.email !== undefined) {
    let contacts: Raw[] = [...arr(current?.contacts)];
    const setContact = (system: 'phone' | 'email', value: string | undefined) => {
      if (value === undefined) return;
      const i = contacts.findIndex((c) => c.system === system);
      const prov = provenanceOf(labels[system], 'patient_reported');
      if (!value) contacts = contacts.filter((_, j) => j !== i);
      else if (i >= 0) contacts[i] = { ...contacts[i], value, ...prov, ...(prov.verificationStatus !== 'verified' ? { verifiedAt: undefined } : {}) };
      else contacts.push({ system, value, use: system === 'phone' ? 'mobile' : 'home', ...prov });
    };
    setContact('phone', b.phone);
    setContact('email', b.email);
    // PATCH replaces the whole array but reads are masked: never write a masked value back (it would destroy data).
    const masked = contacts.filter((c) => isMaskedValue(c.value));
    if (masked.length) {
      const which = masked.map((c) => (c.system === 'email' ? 'email' : 'phone')).join(' and ');
      throw new Error(`Re-enter the current ${which} too: contact details are shown masked, and saving replaces all contact details.`);
    }
    out.contacts = contacts.map(stripUndefined);
  }
  if (b.address !== undefined) {
    const rest = arr(current?.addresses).slice(1);
    out.addresses = b.address
      ? [
          stripUndefined({
            line: [b.address.line1, b.address.line2].filter((x): x is string => !!x),
            city: b.address.city,
            state: b.address.state || undefined,
            postalCode: b.address.postalCode,
            country: 'US',
            ...provenanceOf(labels.address, 'patient_reported'),
          }),
          ...rest,
        ]
      : rest;
  }
  return out;
}
const stripUndefined = (o: Raw) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));

/** Fields core-api cannot store (strict schema). The UI hides these editors for backend records. */
export const UNSUPPORTED_PATIENT_FIELDS = ['pronouns', 'interpreterNeeded', 'preferredChannel', 'emergencyContact'] as const;

/* ---------------- Duplicate candidates (409) ---------------- */

const FEATURE_LABEL: Record<string, string> = {
  givenName: 'Given name', familyName: 'Family name', birthDate: 'Date of birth', contact: 'Phone or email', identifier: 'Identifier', sex: 'Sex', address: 'Address', twin: 'Twin indicator',
};
export const bandOf = (score: number): DuplicateCandidate['band'] => (score >= 0.9 ? 'high' : score >= 0.8 ? 'medium' : 'low');
export function reasonsOf(features: unknown): DuplicateCandidate['reasons'] {
  return arr(features)
    .filter(isObj)
    .filter((f) => !['algorithm', 'method', 'version', 'threshold'].includes(str(f.feature)))
    .map((f) => {
      const s = Number(f.score) || 0;
      return { feature: FEATURE_LABEL[str(f.feature)] ?? str(f.feature), match: s >= 1 ? 'exact' : s >= 0.6 ? 'fuzzy' : 'mismatch', weight: s } as DuplicateCandidate['reasons'][number];
    });
}

/** Backend {patientId,score,method,features[]} (+ optional fetched Patient) → UI DuplicateCandidate. */
export function normalizeDuplicateCandidate(c: unknown, detail?: Patient | null): DuplicateCandidate {
  const raw: Raw = isObj(c) ? c : {};
  if (Array.isArray(raw.reasons) && !Array.isArray(raw.features)) return raw as DuplicateCandidate;
  const score = Number(raw.score) || 0;
  return {
    patientId: str(raw.patientId),
    method: optStr(raw.method),
    display: detail?.name.display ?? 'Existing record',
    dob: detail?.dob ?? '',
    mrn: detail?.mrn ?? '',
    score,
    band: bandOf(score),
    reasons: reasonsOf(raw.features),
  };
}

/* ---------------- Identity candidates / merge (R-043) ---------------- */

const DISPOSITION_STATUS: Record<string, IdentityCandidate['status']> = { distinct_patient: 'not_duplicate', confirmed_duplicate: 'merge_requested', defer: 'deferred' };

/** Placeholder second record for candidates raised by a blocked registration (no patient was created). */
const attemptItem = (id: string): PatientListItem => ({
  id: `attempt:${id}`, mrn: '—', name: { family: 'Registration attempt', given: [], display: 'Registration attempt (not created)' }, dob: '', age: '', sex: 'unknown',
  identityStatus: 'unverified', deceased: false, sensitive: false,
});
const unknownItem = (id: string): PatientListItem => ({
  id, mrn: '', name: { family: 'Restricted or unavailable record', given: [], display: 'Restricted or unavailable record' }, dob: '', age: '', sex: 'unknown',
  identityStatus: 'unverified', deceased: false, sensitive: false,
});

export function normalizeIdentityCandidate(c: unknown, people: Record<string, PatientListItem | undefined> = {}): IdentityCandidate {
  const raw: Raw = isObj(c) ? c : {};
  if (isObj(raw.patientA)) return raw as IdentityCandidate;
  const score = Number(raw.score) || 0;
  const a = str(raw.patientId);
  const b = optStr(raw.candidatePatientId);
  const status: IdentityCandidate['status'] = raw.status === 'pending' ? 'open' : DISPOSITION_STATUS[str(raw.disposition)] ?? (raw.status === 'merged' ? 'merged' : 'not_duplicate');
  return {
    id: str(raw.id),
    rowVersion: rv(raw.rowVersion),
    patientA: people[a] ?? unknownItem(a),
    patientB: b ? people[b] ?? unknownItem(b) : attemptItem(str(raw.id)),
    hasPatientB: !!b,
    registrationId: optStr(raw.registrationId) ?? null,
    score,
    band: bandOf(score),
    reasons: reasonsOf(raw.features),
    status,
    disposition: (raw.disposition as IdentityCandidate['disposition']) ?? null,
    reasonCode: optStr(raw.reasonCode) ?? null,
    createdAt: str(raw.createdAt),
  };
}

export function normalizeMerge(m: unknown): PatientMerge {
  const raw: Raw = isObj(m) ? m : {};
  if ('survivorId' in raw) return raw as PatientMerge;
  return {
    id: str(raw.id),
    rowVersion: rv(raw.rowVersion),
    survivorId: str(raw.targetPatientId),
    mergedId: str(raw.sourcePatientId),
    status: raw.reversedAt ? 'unmerged' : 'completed',
    createdAt: str(raw.createdAt),
    actor: str(raw.createdBy),
    reasonCode: optStr(raw.reasonCode),
  };
}

/** problem.resources from ETNAS_SAFETY_CHECK on unmerge (each needs an explicit assignment). */
export function unmergeReferences(e: unknown): UnmergeReference[] {
  if (!(e instanceof ApiError) || e.code !== 'ETNAS_SAFETY_CHECK') return [];
  return arr((e.problem as unknown as Raw).resources)
    .filter(isObj)
    .map((r) => ({ resourceType: str(r.resourceType), resourceId: str(r.resourceId), patientId: str(r.patientId), ambiguous: !!r.ambiguous }));
}

/* ---------------- Identifiers ---------------- */

export function normalizeIdentifier(i: unknown): PatientIdentifier {
  const raw: Raw = isObj(i) ? i : {};
  if (raw.type && 'verified' in raw) return raw as PatientIdentifier;
  const sys = str(raw.system);
  const type: PatientIdentifier['type'] =
    sys === MRN_SYSTEM ? 'MRN' : /ssn/i.test(sys) ? 'SSN' : /member|payer|coverage/i.test(sys) ? 'MEMBER_ID' : /driver|dl/i.test(sys) ? 'DRIVERS_LICENSE' : 'EXTERNAL_MRN';
  return { system: sys, type, value: str(raw.value), assigningAuthority: str(raw.assigningAuthority), verified: raw.verificationStatus === 'verified', lastVerifiedAt: optStr(raw.verifiedAt) ?? null };
}

/* ---------------- Consent / proxy / labels / relationships (R-042) ---------------- */

export const SENSITIVITY_LABEL: Record<string, string> = {
  behavioral_health: 'Behavioral health', part2_sud: 'Substance use disorder (42 CFR Part 2)', psychotherapy: 'Psychotherapy notes', reproductive_health: 'Reproductive health',
  hiv_sti: 'HIV or STI', genetics: 'Genetic information', domestic_violence: 'Sexual or domestic violence', adolescent_confidential: 'Adolescent-confidential services',
  legal: 'Legal', confidential: 'Confidential',
};
const CATEGORY_WORD: Record<string, string> = { '*': 'All record categories', demographics: 'Demographics', appointments: 'Appointments', medications: 'Medications', results: 'Results', ...SENSITIVITY_LABEL };
export const categoryLabel = (c: string) => CATEGORY_WORD[c] ?? c.replace(/[_.]/g, ' ').replace(/^./, (x) => x.toUpperCase());
const AUTHORITY_WORD: Record<string, string> = { self: 'Patient (self)', legal_guardian: 'Legal guardian', authorized_representative: 'Authorized representative' };

const consentStatusOf = (s: string): ConsentStatus =>
  (['requested', 'identity_verified', 'pending_approval', 'active', 'suspended', 'revoked', 'expired'] as string[]).includes(s) ? (s as ConsentStatus) : s === 'rejected' ? 'revoked' : 'expired';

export function normalizeConsent(c: unknown): Consent {
  const raw: Raw = isObj(c) ? c : {};
  if (isObj(raw.category)) return raw as Consent;
  const categories = arr<string>(raw.categories);
  const purposes = arr<string>(raw.purposes);
  const operations = arr<string>(raw.operations);
  const effect = raw.effect === 'deny' ? 'deny' : 'permit';
  const expired = raw.status === 'active' && raw.expiresAt && new Date(str(raw.expiresAt)).getTime() < Date.now();
  const cats = categories.map(categoryLabel).join(', ') || 'No categories';
  return {
    id: str(raw.id),
    patientId: str(raw.patientId),
    rowVersion: rv(raw.rowVersion),
    category: { system: 'urn:etnas:consent-effect', code: effect, display: `${effect === 'deny' ? 'Restriction' : 'Permission'}: ${cats}` },
    purpose: purposes[0] ?? '',
    purposes,
    scope: `${operations.includes('*') ? 'All actions' : operations.join(', ') || 'No actions'} · ${str(raw.jurisdiction, 'US')}`,
    recipients: arr<string>(raw.recipients).map((r) => (r === '*' ? 'Everyone in the organization' : r)),
    dataCategories: categories.map((x) => (x === '*' ? 'all' : x)),
    status: expired ? 'expired' : consentStatusOf(str(raw.status)),
    backendStatus: optStr(raw.status),
    effectiveFrom: dateOnly(raw.effectiveFrom ?? raw.createdAt),
    effectiveTo: optStr(raw.expiresAt) ? dateOnly(raw.expiresAt) : null,
    grantor: `${AUTHORITY_WORD[str(raw.authority)] ?? str(raw.authority)}${raw.authority && raw.authority !== 'self' && raw.grantorId ? ` · ${str(raw.grantorId).slice(0, 8)}` : ''}`,
    authority: raw.authority,
    effect,
    operations,
    jurisdiction: optStr(raw.jurisdiction),
    policyVersion: str(raw.policyVersion),
    recordedBy: str(raw.createdBy ?? raw.recordedBy),
    recordedAt: str(raw.createdAt ?? raw.effectiveFrom),
    revokedAt: optStr(raw.revokedAt) ?? null,
    evidence: optStr(raw.signatureEvidence) ? `${str(raw.signatureEvidence).slice(0, 19)}…` : undefined,
  };
}

const PROXY_REL_WORD: Record<string, string> = { parent: 'Parent', guardian: 'Guardian', caregiver: 'Caregiver', authorized_representative: 'Authorized representative' };

export function normalizeProxy(p: unknown, patientDisplay = ''): ProxyAuthority {
  const raw: Raw = isObj(p) ? p : {};
  if (Array.isArray(raw.scopes)) return raw as ProxyAuthority;
  const status = str(raw.status);
  const expired = raw.expiresAt && new Date(str(raw.expiresAt)).getTime() < Date.now();
  return {
    id: str(raw.id),
    patientId: str(raw.patientId),
    rowVersion: rv(raw.rowVersion),
    patientDisplay,
    proxyName: str(raw.proxyName) || `Portal account ${str(raw.proxyActorId).slice(0, 8)}`,
    proxyUserId: optStr(raw.proxyActorId) ?? null,
    relationship: PROXY_REL_WORD[str(raw.relationship)] ?? str(raw.relationship),
    scopes: arr<string>(raw.categories).map((c) => ({ category: categoryLabel(c), perm: 'allow' as const })),
    actions: arr<string>(raw.actions),
    status: status === 'active' && expired ? 'expired' : status === 'active' && (raw.emancipated || raw.custodyRestricted) ? 'suspended' : consentStatusOf(status),
    effectiveFrom: dateOnly(raw.effectiveFrom),
    expiresAt: optStr(raw.expiresAt) ? dateOnly(raw.expiresAt) : null,
    evidence: str(raw.evidence),
    ageTransitionAt: optStr(raw.ageTransitionAt) ? dateOnly(raw.ageTransitionAt) : null,
  };
}

export function normalizeSensitivityLabel(l: unknown): SensitivityLabel {
  const raw: Raw = isObj(l) ? l : {};
  if (isObj(raw.label)) return raw as SensitivityLabel;
  const cat = str(raw.category);
  return {
    id: str(raw.id),
    patientId: str(raw.patientId),
    label: { system: 'urn:etnas:sensitivity', code: cat, display: SENSITIVITY_LABEL[cat] ?? cat } as Coded,
    scope: raw.legalProhibition ? 'Whole record · legal prohibition (never disclosed, no break-glass)' : 'Whole record',
    appliedBy: str(raw.source),
    appliedAt: str(raw.createdAt),
    category: cat,
    legalProhibition: !!raw.legalProhibition,
    rowVersion: rv(raw.rowVersion),
    retiredAt: optStr(raw.retiredAt) ?? null,
    retirementReasonCode: optStr(raw.retirementReasonCode),
  };
}

export function normalizeRelationship(r: unknown): Relationship {
  const raw: Raw = isObj(r) ? r : {};
  if (typeof raw.name === 'string' && 'isGuarantor' in raw) return raw as Relationship;
  return {
    id: str(raw.id),
    patientId: str(raw.patientId),
    name: str(raw.relatedDisplay, 'Linked patient record'),
    relationship: str(raw.relationship),
    relatedPatientId: optStr(raw.relatedPatientId) ?? null,
    isEmergencyContact: raw.relationship === 'emergency_contact',
    isGuarantor: false,
    source: optStr(raw.source),
    verificationStatus: raw.verificationStatus,
  };
}

/* ---------------- Break-glass + access check (R-008) ---------------- */

export const BREAK_GLASS_REASON_CODE: Record<BreakGlassReason, string> = {
  emergency: 'EMERGENCY_CARE',
  covering: 'COVERING_CLINICIAN',
  safety: 'IMMEDIATE_SAFETY_RISK',
};
const REASON_FROM_CODE: Record<string, BreakGlassReason> = Object.fromEntries(Object.entries(BREAK_GLASS_REASON_CODE).map(([k, v]) => [v, k as BreakGlassReason]));
export const BREAK_GLASS_SCOPES: { id: BreakGlassScope; label: string }[] = [
  { id: 'patient.read', label: 'Demographics and banner' },
  { id: 'note.read', label: 'Notes' },
  { id: 'medication.read', label: 'Medications' },
  { id: 'allergy.read', label: 'Allergies' },
  { id: 'result.read', label: 'Results' },
  { id: 'order.read', label: 'Orders' },
  { id: 'document.read', label: 'Documents' },
];
const REVIEW_STATUS: Record<string, BreakGlassGrant['review']['status']> = {
  pending: 'pending', appropriate: 'appropriate', inappropriate: 'inappropriate', needs_follow_up: 'needs_followup', needs_followup: 'needs_followup',
};

export function normalizeBreakGlassGrant(g: unknown, patientDisplay = ''): BreakGlassGrant {
  const raw: Raw = isObj(g) ? g : {};
  if (isObj(raw.actor) && isObj(raw.review)) return raw as BreakGlassGrant;
  const scope = arr<string>(raw.scope);
  const ended = optStr(raw.endedAt) ?? null;
  const expiresAt = str(raw.expiresAt);
  const expired = !ended && expiresAt && new Date(expiresAt).getTime() <= Date.now();
  return {
    id: str(raw.id),
    rowVersion: rv(raw.rowVersion),
    patientId: str(raw.patientId),
    patientDisplay: str(raw.patientDisplay, patientDisplay),
    actor: { id: str(raw.actorId), display: str(raw.actorDisplay) },
    reason: REASON_FROM_CODE[str(raw.reasonCode)] ?? str(raw.reasonCode ?? raw.reason),
    justification: '',
    scope,
    purpose: (str(raw.purpose, 'EMERGENCY') as PurposeOfUse),
    startedAt: str(raw.startedAt ?? raw.createdAt),
    expiresAt,
    endedAt: ended,
    status: ended ? 'ended' : expired ? 'expired' : 'active',
    visible: scope.map((s) => BREAK_GLASS_SCOPES.find((x) => x.id === s)?.label ?? s),
    blocked: [],
    accessedResources: Number(raw.accessedResources) || 0,
    review: { status: REVIEW_STATUS[str(raw.reviewStatus)] ?? 'pending', by: optStr(raw.reviewerId), note: optStr(raw.reviewReasonCode) },
  };
}

/** Deny reasons that emergency access can lift (decide(): break-glass exempts care-relationship + sensitivity). */
/**
 * ACCESS_DENIED: public decisions now hide the detailed reason (contract-changes R-015/R-107) — treated as possibly
 * breakable; POST /break-glass still refuses legal prohibitions with a clear 403.
 */
const BREAKABLE = new Set(['SENSITIVITY_RESTRICTED', 'CARE_RELATIONSHIP_REQUIRED', 'ACCESS_DENIED']);
const PASS = new Set(['PERMISSION_AND_CONTEXT_VALID', 'ACCESS_ALLOWED']);
/** Restricted sensitivity categories → chart index sections that may hold them. */
const CATEGORY_SECTIONS: Record<string, string[]> = {
  notes: ['notes'], results: ['results'], medications: ['medications', 'prescriptions'], allergies: ['allergies'], documents: ['documents'], messages: [],
  orders: ['orders'], problems: ['problems'], vitals: ['vitals'], encounters: ['timeline'],
  chart: ['summary', 'timeline', 'problems', 'medications', 'allergies', 'vitals', 'results', 'notes', 'orders', 'prescriptions', 'documents'],
  behavioral_health: ['notes', 'medications', 'problems', 'documents'], part2_sud: ['notes', 'medications', 'problems', 'documents'], psychotherapy: ['notes'],
  reproductive_health: ['notes', 'problems', 'results', 'documents'], hiv_sti: ['problems', 'results', 'medications', 'notes'], genetics: ['results', 'documents'],
  domestic_violence: ['notes', 'documents'], adolescent_confidential: ['notes', 'problems', 'medications', 'results'], legal: ['documents'], confidential: ['notes', 'documents'],
};
const REASON_WORD: Record<string, string> = {
  PERMISSION_AND_CONTEXT_VALID: 'Permission and context valid', SENSITIVITY_RESTRICTED: 'Sensitive records restricted', CARE_RELATIONSHIP_REQUIRED: 'No care relationship',
  LEGAL_PROHIBITION: 'Legal prohibition', CONSENT_DENIED: 'Patient consent denies this use', PURPOSE_NOT_PERMITTED: 'Purpose of use not permitted', PERMISSION_MISSING: 'Permission missing',
};

/**
 * Compose the UI AccessCheck from the raw backend decision {decision,policyVersion,reasons,obligations} and the
 * caller's active grants. Extended backend fields (canView, breakGlassEligible, restrictedSections, activeBreakGlass)
 * win when present.
 */
export function normalizeAccessCheck(input: unknown, patientId: string, grant: BreakGlassGrant | null = null): AccessCheck {
  const raw: Raw = isObj(input) ? input : {};
  // UI/mock shape (decision.inputs) passes through; the composed backend shape is normalized below.
  if (typeof raw.canView === 'boolean' && isObj(raw.decision) && Array.isArray(raw.decision.inputs)) return raw as AccessCheck;
  const d: Raw = isObj(raw.decision) ? raw.decision : raw;
  const verdict = str(d.decision ?? raw.decision);
  const reasons = arr<string>(d.reasons ?? raw.reasons);
  const permit = verdict === 'permit' || verdict === 'allow';
  const eligible =
    typeof raw.breakGlassEligible === 'boolean' ? raw.breakGlassEligible : !permit && reasons.length > 0 && reasons.every((r) => BREAKABLE.has(r)) && !reasons.includes('LEGAL_PROHIBITION');
  const active = isObj(raw.activeBreakGlass)
    ? normalizeBreakGlassGrant({ ...(grant ?? {}), patientId, purpose: 'EMERGENCY', ...raw.activeBreakGlass }, grant?.patientDisplay)
    : grant;
  const decision: PolicyDecision = {
    decision: permit ? 'allow' : 'deny',
    action: 'patient.read',
    inputs: reasons.map((r) => ({ label: REASON_WORD[r] ?? r, value: r, result: PASS.has(r) ? 'pass' : BREAKABLE.has(r) ? 'warn' : 'fail' })),
    obligations: arr<string>(d.obligations ?? raw.obligations),
    policyVersion: str(d.policyVersion ?? raw.policyVersion),
    correlationId: optStr(d.correlationId ?? raw.correlationId),
    reason: reasons.join(', '),
  };
  return {
    patientId,
    canView: typeof raw.canView === 'boolean' ? raw.canView : permit,
    relationship: (optStr(raw.relationship) as AccessCheck['relationship']) ?? (reasons.includes('CARE_RELATIONSHIP_REQUIRED') ? 'none' : 'care_team'),
    reasons,
    // Backend reports restricted CATEGORIES (notes, results, part2_sud, …, or `chart`); the index needs section ids.
    restrictedSections: [...new Set(arr<string>(raw.restrictedSections ?? raw.restrictedCategories).flatMap((c) => CATEGORY_SECTIONS[c] ?? [c]))],
    breakGlassEligible: !!eligible && !active,
    activeBreakGlass: active,
    decision,
  };
}

/** UI break-glass input → strict backend body. */
export function toBreakGlassBody(b: BreakGlassRequest) {
  const reason = (BREAK_GLASS_REASON_CODE as Record<string, string>)[b.reason] ?? b.reason;
  const scope = b.scope?.length ? b.scope : (['patient.read'] as BreakGlassScope[]);
  return {
    patientId: b.patientId,
    reason,
    purpose: 'EMERGENCY' as const,
    scope: scope.includes('patient.read') ? scope : (['patient.read', ...scope] as BreakGlassScope[]),
    durationMinutes: Math.min(60, Math.max(1, Math.round(b.durationMinutes || 30))),
    warningAcknowledged: true as const,
  };
}

/* =====================================================================================================
 * API surface
 * ===================================================================================================== */

async function fetchPatientSafe(id: string): Promise<Patient | null> {
  try {
    const r = await getVersioned<unknown>(`/patients/${seg(id)}`);
    return normalizePatient(r.data, r.etag);
  } catch {
    return null;
  }
}

export const patientsApi = {
  /**
   * Audited search. Prohibited/restricted patients never appear. MRN/DOB patterns and "Family, Given" are turned into
   * the right backend params (R-111); a two-word name with no hits is retried in the other order.
   */
  search: async (p: PatientSearchParams): Promise<Page<PatientListItem>> => {
    const { query, reasons, reversed } = buildPatientSearch(p);
    const run = async (q: Query) => {
      const page = normalizePage(await get<unknown>('/patients', q), normalizePatientListItem);
      return { ...page, items: page.items.map((i) => (i.matchReasons ? i : { ...i, matchReasons: reasons })) };
    };
    const first = await run(query);
    if (!first.items.length && reversed && !p.cursor) return run({ ...query, q: reversed });
    return first;
  },
  /**
   * 409 ETNAS_DUPLICATE_CANDIDATES unless dispositionOverride is supplied. The problem's candidates are enriched
   * (GET /patients/{id}) and rewritten into the UI DuplicateCandidate shape, so `err.candidates` is ready to render.
   */
  create: async (body: CreatePatientRequest): Promise<Patient> => {
    try {
      const r = await postVersioned<unknown>('/patients', toCreatePatientBody(body), { idempotent: true });
      return normalizePatient(r.data, r.etag);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ETNAS_DUPLICATE_CANDIDATES') {
        const list = arr((e.problem as unknown as Raw).candidates);
        if (list.some((c) => isObj(c) && Array.isArray(c.features))) {
          const details = await Promise.all(list.map((c) => fetchPatientSafe(str(c.patientId))));
          (e.problem as unknown as Raw).candidates = list.map((c, i) => normalizeDuplicateCandidate(c, details[i]));
        }
      }
      throw e;
    }
  },
  get: async (id: string, opts: { purpose?: PurposeOfUse } = {}) => {
    const r = await getVersioned<unknown>(`/patients/${seg(id)}`, undefined, opts);
    return { data: normalizePatient(r.data, r.etag), etag: r.etag };
  },
  /**
   * PATCH with If-Match. Pass `current` so contacts/addresses are merged (backend replaces whole arrays). For backend
   * records only backend fields are sent; pronouns/channel/interpreter/emergency contact are not stored by core-api.
   */
  update: async (id: string, body: PatchPatientRequest, etag: string | null, current?: Patient | null) => {
    const wire = current?.extendedDemographics ? body : toPatchPatientBody(body, current);
    const r = await patch<unknown>(`/patients/${seg(id)}`, wire, { ifMatch: etag ?? current?.etag ?? null });
    return { data: normalizePatient(r.data, r.etag), etag: r.etag };
  },
  /** Full identifiers (SSN etc). Every call is an audited reveal. */
  identifiers: async (id: string) => normalizePage(await get<unknown>(`/patients/${seg(id)}/identifiers`), normalizeIdentifier),
  /**
   * Composed access check: GET /patients/{id}/access-check (raw policy decision) + the caller's active break-glass
   * grant for this patient (re-evaluated under EMERGENCY purpose when a grant is active).
   */
  accessCheck: async (id: string): Promise<AccessCheck> => {
    let grant: BreakGlassGrant | null = null;
    try {
      grant = (await breakGlassApi.active()).items.find((g) => g.patientId === id && g.status === 'active') ?? null;
    } catch {
      grant = null;
    }
    const raw = await get<unknown>(`/patients/${seg(id)}/access-check`, undefined, grant ? { purpose: 'EMERGENCY' } : {});
    return normalizeAccessCheck(raw, id, grant);
  },
  relationships: async (id: string) => normalizePage(await get<unknown>(`/patients/${seg(id)}/relationships`), normalizeRelationship),
  addRelationship: async (id: string, body: CreateRelationshipRequest) =>
    normalizeRelationship(await post<unknown>(`/patients/${seg(id)}/relationships`, body, { idempotent: true })),
};

async function listPeople(ids: string[]): Promise<Record<string, PatientListItem | undefined>> {
  const uniq = [...new Set(ids.filter(Boolean))];
  const got = await Promise.all(uniq.map((id) => fetchPatientSafe(id)));
  return Object.fromEntries(uniq.map((id, i) => [id, got[i] ?? undefined]));
}

export const IDENTITY_REASON_CODES: { code: string; label: string; for: IdentityDispositionRequest['disposition'][] }[] = [
  { code: 'DIFFERENT_IDENTIFIERS_SEEN', label: 'Different IDs or identifiers seen', for: ['distinct_patient'] },
  { code: 'TWIN_OR_MULTIPLE_BIRTH', label: 'Twin or multiple birth', for: ['distinct_patient'] },
  { code: 'DIFFERENT_DEMOGRAPHICS_CONFIRMED', label: 'Different demographics confirmed with the patient', for: ['distinct_patient'] },
  { code: 'SAME_PERSON_ID_MATCH', label: 'Same person: photo ID and identifiers match', for: ['confirmed_duplicate'] },
  { code: 'SAME_PERSON_PATIENT_CONFIRMED', label: 'Same person: confirmed with the patient', for: ['confirmed_duplicate'] },
  { code: 'NEEDS_MORE_INFORMATION', label: 'Needs more information', for: ['defer'] },
  { code: 'AWAITING_PATIENT_CONTACT', label: 'Waiting to reach the patient', for: ['defer'] },
];

export const identityApi = {
  registration: (id: string) => get<{ id: string; proposedDemographics: Record<string, unknown>; status: string; patientId?: string | null; candidates: { status: string; disposition: string | null }[] }>(`/registration-requests/${seg(id)}`),
  completeRegistration: async (proposed: Record<string, unknown>) => normalizePatient(await post<unknown>('/patients', proposed, { idempotent: true })),
  merges: async (patientId?: string, p: { limit?: number; cursor?: string } = {}) => {
    const page = await get<Page<unknown>>('/patient-merges', { patientId, limit: 100, ...p });
    return { ...page, items: page.items.map(normalizeMerge) };
  },
  /**
   * Candidates with both records resolved (GET /patients/{id} per record). The backend list has no status filter:
   * filtered here (open = pending).
   */
  candidates: async (p: ListParams & { status?: string } = {}): Promise<Page<IdentityCandidate>> => {
    const { status, ...rest } = p;
    const page = await get<unknown>('/identity-candidates', { ...rest });
    const items = arr(isObj(page) ? page.items : []);
    const backend = items.some((c) => isObj(c) && 'candidatePatientId' in c);
    const people = backend ? await listPeople(items.flatMap((c) => [str(c.patientId), str(c.candidatePatientId)])) : {};
    const list = items.map((c) => normalizeIdentityCandidate(c, people));
    const filtered = status ? list.filter((c) => c.status === status) : list;
    return { items: filtered.sort((a, b) => b.score - a.score), nextCursor: isObj(page) ? (page.nextCursor as string | null) ?? null : null };
  },
  disposition: async (c: Pick<IdentityCandidate, 'id' | 'rowVersion'> | string, body: IdentityDispositionRequest, etag?: string | null) => {
    const id = typeof c === 'string' ? c : c.id;
    const ifMatch = etag ?? (typeof c === 'string' ? null : etagFromRow(c.rowVersion));
    return normalizeIdentityCandidate(await post<unknown>(`/identity-candidates/${seg(id)}:disposition`, body, { idempotent: true, ifMatch }));
  },
  /**
   * Merge `source` into `target` (target survives). If-Match = source ETag; the target's rowVersion goes in the body.
   * Step-up required.
   */
  merge: async (source: Pick<Patient, 'id' | 'rowVersion' | 'etag'>, target: Pick<Patient, 'id' | 'rowVersion'>, reasonCode: string, label?: string) => {
    const body: MergePatientsRequest = { sourcePatientId: source.id, targetPatientId: target.id, targetVersion: Number(target.rowVersion ?? 0), reasonCode };
    const r = await postVersioned<unknown>('/patient-merges', body, { idempotent: true, ifMatch: source.etag ?? etagFromRow(source.rowVersion), stepUpLabel: label ?? 'Merge patient records' });
    return { data: normalizeMerge(r.data), etag: r.etag };
  },
  /** Unmerge needs an assignment for every downstream reference: on ETNAS_SAFETY_CHECK read them with `unmergeReferences(err)`. */
  unmerge: async (id: string, reasonCode: string, assignments: UnmergeAssignment[], etag: string | null) => {
    const r = await postVersioned<unknown>(`/patient-merges/${seg(id)}:unmerge`, { reasonCode, assignments }, { idempotent: true, ifMatch: etag, stepUpLabel: 'Reverse a patient merge' });
    return { data: normalizeMerge(r.data), etag: r.etag };
  },
};

/** SHA-256 evidence digest in the backend format `sha256:<64 hex>`. */
export async function sha256Evidence(data: string | ArrayBuffer | Blob): Promise<string> {
  const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data instanceof Blob ? await data.arrayBuffer() : data;
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return 'sha256:' + [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export const privacyApi = {
  consentGrantors: (patientId: string) => get<Page<{ id: string; display: string; authority: 'self' }>>(`/patients/${seg(patientId)}/consent-grantors`),
  consents: async (patientId: string) => normalizePage(await get<unknown>(`/patients/${seg(patientId)}/consents`), normalizeConsent),
  /** Step-up + Idempotency-Key. Starts as `requested`; an independent privacy/HIM reviewer activates it (reviewConsent). */
  createConsent: async (patientId: string, body: CreateConsentRequest, supersedesEtag?: string | null) =>
    normalizeConsent(
      await post<unknown>(`/patients/${seg(patientId)}/consents`, body, { idempotent: true, ifMatch: body.supersedesId ? supersedesEtag ?? null : null, stepUpLabel: 'Record a consent directive' }),
    ),
  /** Independent review: approve activates, reject closes. reasonCode + If-Match + step-up. */
  reviewConsent: async (c: Pick<Consent, 'id' | 'rowVersion'>, decision: 'approve' | 'reject', reasonCode: string) =>
    normalizeConsent(await post<unknown>(`/consents/${seg(c.id)}:review`, { decision, reasonCode }, { idempotent: true, ifMatch: etagFromRow(c.rowVersion), stepUpLabel: 'Review a consent directive' })),
  /** reasonCode + If-Match (W/"rowVersion" of the consent). Step-up. */
  revokeConsent: async (c: Pick<Consent, 'id' | 'rowVersion'>, reasonCode: string) =>
    normalizeConsent(await post<unknown>(`/consents/${seg(c.id)}:revoke`, { reasonCode }, { idempotent: true, ifMatch: etagFromRow(c.rowVersion), stepUpLabel: 'Revoke a consent directive' })),
  proxies: async (patientId: string, patientDisplay = '') =>
    normalizePage(await get<unknown>(`/patients/${seg(patientId)}/proxy-authorities`), (x) => normalizeProxy(x, patientDisplay)),
  /** Step-up required. */
  createProxy: async (patientId: string, body: CreateProxyAuthorityRequest, patientDisplay = '') =>
    normalizeProxy(
      await post<unknown>(`/patients/${seg(patientId)}/proxy-authorities`, body, { idempotent: true, stepUpLabel: `Grant proxy access${patientDisplay ? ' to ' + patientDisplay : ''}` }),
      patientDisplay,
    ),
  revokeProxy: async (p: Pick<ProxyAuthority, 'id' | 'rowVersion'>, reasonCode: string) =>
    normalizeProxy(await post<unknown>(`/proxy-authorities/${seg(p.id)}:revoke`, { reasonCode }, { idempotent: true, ifMatch: etagFromRow(p.rowVersion), stepUpLabel: 'Revoke proxy access' })),
  sensitivityLabels: async (patientId: string) => normalizePage(await get<unknown>(`/patients/${seg(patientId)}/sensitivity-labels`), normalizeSensitivityLabel),
  retireSensitivityLabel: async (id: string, reasonCode: string, etag: string | null) => normalizeSensitivityLabel((await postVersioned<unknown>(`/sensitivity-labels/${seg(id)}:retire`, { reasonCode }, { idempotent: true, ifMatch: etag, stepUpLabel: 'Retire a sensitivity label' })).data),
  addSensitivityLabel: async (patientId: string, body: CreateSensitivityLabelRequest) =>
    normalizeSensitivityLabel(await post<unknown>(`/patients/${seg(patientId)}/sensitivity-labels`, body, { idempotent: true })),
};

export const breakGlassApi = {
  /** Requires step-up and the EMERGENCY purpose header; returns a time-boxed grant. */
  start: async (body: BreakGlassRequest, patientDisplay: string) =>
    normalizeBreakGlassGrant(
      await post<unknown>('/break-glass', toBreakGlassBody(body), { idempotent: true, purpose: 'EMERGENCY', stepUpLabel: `Emergency access to ${patientDisplay}` }),
      patientDisplay,
    ),
  active: async () => normalizePage(await get<unknown>('/break-glass/active'), (g) => normalizeBreakGlassGrant(g)),
  /** If-Match required: pass the grant (or its ETag); with only an id the current version is looked up first. */
  end: async (grant: string | Pick<BreakGlassGrant, 'id' | 'rowVersion'>, etag?: string | null) => {
    const id = typeof grant === 'string' ? grant : grant.id;
    let ifMatch = etag ?? (typeof grant === 'string' ? null : etagFromRow(grant.rowVersion));
    if (!ifMatch) {
      const cur = (await breakGlassApi.active()).items.find((g) => g.id === id);
      ifMatch = etagFromRow(cur?.rowVersion);
    }
    return normalizeBreakGlassGrant(await post<unknown>(`/break-glass/${seg(id)}:end`, {}, { idempotent: true, ifMatch }));
  },
};
